import {
  createColumnHelper, flexRender, getCoreRowModel,
  getSortedRowModel, SortingState, useReactTable,
} from "@tanstack/react-table";
import React, { useContext } from "react";
import { convertToFloat } from "utils/formatNumber";
import { PaymentRowObj } from "..";
import InfoModal from "./InfoModalDash";
import RefundModal from "./RefundModalDash";
import StatusRender from "./StatusRender";
import ShortTruncateCopy from "components/common/RegistrationLinkCopy";
import { getImage } from "utils/commonFunction";
import { ClientContext } from "ClientProvider";
import CustomToolTip from "components/tooltips/new";
import { BsArrowDown, BsArrowUp } from "react-icons/bs";
import { RiArrowUpDownFill } from "react-icons/ri";
import { Link } from "react-router-dom";
import Card from "components/card";

const columnHelper = createColumnHelper<PaymentRowObj>();

function PaymentsListDashboard(props: {
  tableData: any;
  fetchPayments: () => void;
  isLoading: boolean;
  paymentName: boolean;
  allGateways: any[];
}) {
  const { tableData, fetchPayments, isLoading, paymentName, allGateways } = props;
  const { singleRoleData } = useContext(ClientContext);

  const columns = [
    columnHelper.accessor("client", {
      id: "client",
      header: () => <span className="pld-th-lbl">Client</span>,
      cell: (info: any) => (
        <div className="pld-cur-cell">
          <div className="pld-avatar">{info.getValue()?.charAt(0)?.toUpperCase() || "?"}</div>
          <span className="pld-client-name">{info.getValue()}</span>
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.client || "";
        const b = rowB.original.client || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.id).localeCompare(String(rowB.original.id));
      },
    }),
    columnHelper.accessor("amount", {
      id: "amount",
      header: () => <span className="pld-th-lbl">Amount</span>,
      cell: (info: any) => (
        <div className="pld-amount-cell">
          <span className="pld-amount">{convertToFloat(info.getValue())} {info?.row?.original?.currency}</span>
          {info?.row?.original?.converted_amount && info?.row?.original?.converted_currency && (
            <span className="pld-converted">
              {Number(info?.row?.original?.converted_amount).toFixed(2)} {info?.row?.original?.converted_currency}
            </span>
          )}
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const a = Number(rowA.original.amount || 0);
        const b = Number(rowB.original.amount || 0);
        if (a !== b) {
          return a - b;
        }
        return String(rowA.original.id).localeCompare(String(rowB.original.id));
      },
    }),
    columnHelper.accessor("et_id", {
      id: "et_id_",
      header: () => <span className="pld-th-lbl">Transaction ID</span>,
      cell: (info: any) => (
        <div className="pld-ids-cell">
          <ShortTruncateCopy info={info?.getValue()} showCopy={true} />
        </div>
      ),
    }),
    columnHelper.accessor("id", {
      id: "id",
      header: () => <span className="pld-th-lbl">Internal ID</span>,
      cell: (info: any) => (
        <div className="pld-ids-cell">
          <ShortTruncateCopy info={info?.getValue()} showCopy={true} />
        </div>
      ),
    }),
    columnHelper.accessor("gateway_id", {
      id: "gateway_id",
      header: () => <span className="pld-th-lbl">Gateway {paymentName ? "Name" : "Hash ID"}</span>,
      cell: (info: any) => (
        paymentName ? (
          <div className="pld-gateway">
            <img style={{ height: "auto", width: "16px" }} src={getImage(info?.cell?.row?.original?.gateway_name)} alt="" />
            <span className="pld-gateway-name">{info?.cell?.row?.original?.gateway_name}</span>
          </div>
        ) : (
          <ShortTruncateCopy info={info?.cell?.row?.original?.gateway_id} showCopy={true} />
        )
      ),
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.gateway_id || "";
        const b = rowB.original.gateway_id || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.id).localeCompare(String(rowB.original.id));
      },
    }),
    columnHelper.accessor("status", {
      id: "status",
      header: () => <span className="pld-th-lbl">Status</span>,
      cell: (info) => (
        <div className="pld-status-cell">
          {true ? (
            <CustomToolTip
              content={
                info?.row?.original?.payment_history?.length > 0 ? (
                  info?.row?.original?.payment_history?.map((hisData: any, index: number) => {
                    let errorMessage = "";
                    if (["DECLINED", "ERRORED", "FAILED"].includes(info.getValue() as string)) {
                      const gatewayResponse = info?.row?.original?.gateway_response?.gateway_response;
                      const errorKeys = ["errorMessage", "message", "decline_reason", "error_message", "failure_message", "failure_code","unified_message","ResponseDescription","description"];
                      const findLastError = (obj: any): string | null => {
                        let last: string | null = null;
                        const search = (data: any) => {
                          if (!data || typeof data !== "object") return;

                          for (const key of Object.keys(data)) {
                            const value = data[key];

                            // Check known error keys
                            if (
                              errorKeys.includes(key) &&
                              key !== "_content" &&
                              typeof value === "string" &&
                              value.trim()
                            ) {
                              last = value;
                            }

                            // Recursively search nested objects/arrays
                            if (value && typeof value === "object") {
                              search(value);
                            }

                            // Handle JSON stored inside _content
                            if (key === "_content" && typeof value === "string") {
                              try {
                                const parsed = JSON.parse(value);
                                search(parsed);
                              } catch {
                                // Ignore invalid JSON
                              }
                            }
                          }
                        };
                        search(obj); return last;
                      };
                      const err = findLastError(gatewayResponse);
                      errorMessage = typeof err === "string" ? err : "";
                    }
                    return (
                      <Card className="m-2 shadow" key={index}>
                        <div className="border-1 max-w-[70vh] border border-white p-3">
                          {index === 0 && (
                            <p>Created at {hisData?.created_at} by API call from [{hisData?.client} - {hisData?.ip}] with status {hisData?.status}
                              {["DECLINED", "ERRORED", "FAILED"].includes(hisData?.status) && (<><br />Reason: {errorMessage || "Reason not mentioned by the gateway"}</>)}
                            </p>
                          )}
                          {index !== 0 && (
                            <p>Status change from {hisData?.previous_status} to {hisData?.status} at {hisData?.status_changed_date} by {hisData?.client ? `System Api Call ${singleRoleData?.[0]?.payment?.value?.payment_show_method_name ? hisData?.gateway_name : info?.cell?.row?.original?.gateway_id || ""}` : `${hisData?.first_name || ""} ${hisData?.last_name || ""}`}
                              {["DECLINED", "ERRORED", "FAILED"].includes(hisData?.status) && (<><br />Reason: {errorMessage || "Reason not mentioned by the gateway"}</>)}
                            </p>
                          )}
                        </div>
                      </Card>
                    );
                  })
                ) : <span>No history available.</span>
              }
              trigger={<button className="pld-status-btn"><StatusRender status={info?.row?.original?.status} value={info.getValue()} /></button>}
              header={"Status History"}
            />
          ) : (
            <StatusRender status={info?.row?.original?.status} value={info.getValue()} />
          )}
        </div>
      ),
      sortingFn: (rowA, rowB) => {
        const a = rowA.original.status || "";
        const b = rowB.original.status || "";
        const result = a.localeCompare(b);
        if (result !== 0) {
          return result;
        }
        return String(rowA.original.id).localeCompare(String(rowB.original.id));
      },
    }),
    columnHelper.accessor("et_id", {
      id: "et_id",
      header: () => <span className="pld-th-lbl">Actions</span>,
      cell: (info: any) => {
        const enRefund = allGateways?.find((g) => g.id === info?.row?.original?.gateway_id);
        return (
          <div className="pld-actions">
            {singleRoleData?.[0]?.payment?.value?.refund_payment_list && (
              <RefundModal info={info?.row?.original} transactionID={info?.row?.original?.id} fetchPayments={fetchPayments} enRefund={enRefund?.refund} />
            )}
            {(singleRoleData?.[0]?.payment?.value?.view_payment_response_information || singleRoleData?.[0]?.payment?.value?.view_gateway_response_information) && (
              <InfoModal info={info?.row?.original} />
            )}
          </div>
        );
      },
      enableSorting: false,
    }),
  ];

  const columnsNonAction = columns.slice(0, -1);

  const hasActions = singleRoleData?.[0]?.payment?.value?.refund_payment_list || singleRoleData?.[0]?.payment?.value?.view_payment_response_information || singleRoleData?.[0]?.payment?.value?.view_gateway_response_information;
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [data, setData] = React.useState(() => [...(tableData ?? [])]);
  React.useEffect(() => { setData(tableData ?? []); }, [tableData]);

  const table = useReactTable({
    data, columns: hasActions ? columns : columnsNonAction,state: { sorting }, onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(), getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="pld-card">
      <div className="pld-orb" />
      <div className="pld-shine" />

      {/* ── Header ── */}
      <div className="pld-hdr">
        <div className="pld-hl">
          <div className="pld-ico">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
            </svg>
          </div>
          <div>
            <h3 className="pld-title">Recent Transactions</h3>
            <p className="pld-subtitle">Latest payment activity</p>
          </div>
        </div>
        <Link to="/admin/transactions" className="pld-view-btn">
          View all
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M5 12h14M12 5l7 7-7 7"/>
          </svg>
        </Link>
      </div>

      {/*
        TWO-LAYER wrapper — the key fix for scroll + tooltip working together:
        pld-tbl-outer: overflow-x: auto  (horizontal scroll)
        pld-tbl-wrap:  overflow: visible  (tooltip escapes vertically)
        A single element cannot have overflow-x:auto + overflow-y:visible
        because the browser spec forces overflow-y to auto as well.
      */}
      <div className="pld-tbl-outer">
        <div className="pld-tbl-wrap">
          {isLoading ? (
            <div className="pld-loading">
              <div className="pld-spinner" />
              <p className="pld-loading-txt">Loading transactions…</p>
            </div>
          ) : (
            <table className="pld-table">
              <thead>
                {table.getHeaderGroups().map((hg) => (
                  <tr key={hg.id} className="pld-thead-tr">
                    {hg.headers.map((h) => (
                      <th key={h.id} colSpan={h.colSpan}
                        onClick={h.column.getToggleSortingHandler()}
                        className="pld-thead-th">
                        <div className="pld-th-inner">
                          {flexRender(h.column.columnDef.header, h.getContext())}
                          {h.column.getCanSort() && (
                            <span className="pld-sort-ico">
                              {h.column.getIsSorted() === "asc" ? <BsArrowUp size={11} /> : h.column.getIsSorted() === "desc" ? <BsArrowDown size={11} /> : <RiArrowUpDownFill size={11} />}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row, ri) => (
                  <tr key={row.id} className="pld-tbody-tr" style={{ animationDelay: `${ri * 30}ms` }}>
                    {row.getVisibleCells().map((cell) => (
                      <td key={cell.id} className="pld-tbody-td"
                        data-label={
                          cell.column.id === "client"     ? "Client"      :
                          cell.column.id === "amount"     ? "Amount"      :
                          cell.column.id === "et_id_"     ? "Txn ID"      :
                          cell.column.id === "id"         ? "Internal ID" :
                          cell.column.id === "gateway_id" ? "Gateway"     :
                          cell.column.id === "status"     ? "Status"      : "Actions"
                        }>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                ))}
                {table.getRowModel().rows.length === 0 && (
                  <tr><td colSpan={7}>
                    <div className="pld-empty">
                      <div className="pld-empty-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                        </svg>
                      </div>
                      <p className="pld-empty-title">No transactions found</p>
                      <p className="pld-empty-sub">Try adjusting your filters</p>
                    </div>
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800;900&display=swap');

        .pld-card {
          font-family: 'Plus Jakarta Sans', sans-serif;
          border-radius: 28px; display: flex; flex-direction: column;
          position: relative; height: 100%;
          background: linear-gradient(155deg,#f5f8ff 0%,#eef2ff 55%,#f8f9ff 100%);
          border: 1.5px solid #d4dbff;
        }
        .pld-orb {
          position: absolute; top: -60px; right: -60px;
          width: 200px; height: 200px; border-radius: 50%;
          pointer-events: none; opacity: .07;
          background: radial-gradient(circle,#6366f1,transparent);
        }
        .pld-shine {
          position: absolute; inset: 0;
          background: linear-gradient(120deg,transparent 30%,rgba(255,255,255,.5) 50%,transparent 70%);
          animation: pldShine 5s ease-in-out infinite;
          pointer-events: none; border-radius: 28px; z-index: 0;
        }
        @keyframes pldShine { 0%,100%{opacity:0;transform:translateX(-100%)} 50%{opacity:1;transform:translateX(100%)} }

        .pld-hdr {
          padding: 16px 20px 12px; display: flex; align-items: center;
          justify-content: space-between; gap: 10px; flex-wrap: wrap;
          border-bottom: 1px solid rgba(99,102,241,.1); position: relative; z-index: 2;
          border-radius: 28px 28px 0 0;
          background: linear-gradient(155deg,#f5f8ff 0%,#eef2ff 55%,#f8f9ff 100%);
        }
        .pld-hl { display: flex; align-items: center; gap: 11px; }
        .pld-ico {
          width: 42px; height: 42px; border-radius: 14px; flex-shrink: 0;
          display: flex; align-items: center; justify-content: center;
          background: linear-gradient(135deg,#818cf8,#4f46e5);
          box-shadow: 0 6px 20px rgba(99,102,241,.4),inset 0 1.5px 0 rgba(255,255,255,.45);
        }
        .pld-ico svg { filter: drop-shadow(0 1px 3px rgba(0,0,0,.25)); }
        .pld-title { font-size: 13px; font-weight: 800; color: #0d1b3e; letter-spacing: -.2px; line-height: 1.3; margin-bottom: 2px; }
        .pld-subtitle { font-size: 9.5px; font-weight: 700; letter-spacing: .6px; text-transform: uppercase; color: #6366f1; }
        .pld-view-btn {
          display: inline-flex; align-items: center; gap: 6px;
          font-size: 11.5px; font-weight: 800; text-decoration: none;
          padding: 7px 14px; border-radius: 100px;
          background: linear-gradient(135deg,#818cf8,#4f46e5);
          color: #fff; box-shadow: 0 4px 14px rgba(99,102,241,.35);
          transition: box-shadow .15s, transform .15s; white-space: nowrap;
        }
        .pld-view-btn:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(99,102,241,.45); }

        /* ── Two-layer scroll + tooltip fix ── */
        .pld-tbl-outer {
          overflow-x: auto;
          overflow-y: visible;
          -webkit-overflow-scrolling: touch;
          position: relative; z-index: 2;
          border-radius: 0 0 28px 28px;
        }
        .pld-tbl-outer::-webkit-scrollbar { height: 4px; }
        .pld-tbl-outer::-webkit-scrollbar-thumb { border-radius: 4px; background: rgba(99,102,241,.2); }
        .pld-tbl-wrap { overflow: visible; min-width: 100%; }

        .pld-table { width: 100%; border-collapse: collapse; min-width: 860px; }

        .pld-thead-tr { background: linear-gradient(90deg,rgba(99,102,241,.09),rgba(99,102,241,.04)); }
        .pld-thead-th {
          padding: 10px 14px; text-align: left; cursor: pointer;
          user-select: none; white-space: nowrap;
          border-bottom: 1.5px solid rgba(99,102,241,.12);
        }
        .pld-th-inner { display: flex; align-items: center; gap: 5px; }
        .pld-th-lbl { font-size: 10px; font-weight: 800; letter-spacing: .6px; text-transform: uppercase; color: #344563; }
        .pld-sort-ico { opacity: .4; display: flex; align-items: center; transition: opacity .15s; }
        .pld-thead-th:hover .pld-sort-ico { opacity: .9; color: #4f46e5; }

        @keyframes pldFadeIn { from{opacity:0;transform:translateX(-6px)} to{opacity:1;transform:none} }
        .pld-tbody-tr {
          transition: background .12s, transform .12s;
          animation: pldFadeIn .3s ease both;
          border-bottom: 1px solid rgba(99,102,241,.06);
        }
        .pld-tbody-tr:hover { background: rgba(99,102,241,.05); transform: translateX(3px); }
        .pld-tbody-td { padding: 10px 14px; vertical-align: middle; }

        .pld-status-cell { position: relative; }
        .pld-status-cell > * { position: relative; z-index: 9999; }

        .pld-cur-cell { display: flex; align-items: center; gap: 8px; }
        .pld-avatar {
          width: 30px; height: 30px; border-radius: 9px; flex-shrink: 0;
          background: linear-gradient(135deg,#818cf8,#4f46e5);
          box-shadow: 0 3px 10px rgba(99,102,241,.3);
          display: flex; align-items: center; justify-content: center;
          font-size: 11px; font-weight: 800; color: #fff;
        }
        .pld-client-name { font-size: 12px; font-weight: 700; color: #0d2137; white-space: nowrap; }
        .pld-amount-cell { display: flex; flex-direction: column; gap: 2px; }
        .pld-amount { font-size: 12.5px; font-weight: 800; color: #0d2137; text-transform: uppercase; white-space: nowrap; }
        .pld-converted { font-size: 10px; color: #8a96b8; font-weight: 600; white-space: nowrap; }
        .pld-ids-cell { font-weight: 700; color: #0d2137; }
        .pld-gateway { display: flex; align-items: center; gap: 7px; }
        .pld-gateway-name { font-size: 12px; font-weight: 700; color: #344563; white-space: nowrap; }
        .pld-status-btn {
          background: none; border: none; cursor: pointer; padding: 0;
          display: inline-flex; align-items: center; gap: 6px;
          white-space: nowrap; font-weight: 700;
        }
        .pld-actions { display: flex; align-items: center; gap: 6px; }

        .pld-empty {
          display: flex; flex-direction: column; align-items: center;
          justify-content: center; padding: 40px 24px; gap: 10px;
        }
        .pld-empty-icon {
          width: 52px; height: 52px; border-radius: 14px;
          background: rgba(255,255,255,.8); border: 1.5px solid rgba(99,102,241,.15);
          display: flex; align-items: center; justify-content: center; color: #a5b4fc;
        }
        .pld-empty-title { font-size: 13px; font-weight: 800; color: #475080; }
        .pld-empty-sub { font-size: 11.5px; color: #8a96b8; font-weight: 500; text-align: center; }

        .pld-loading {
          display: flex; flex-direction: column; align-items: center;
          justify-content: center; padding: 48px; gap: 12px;
        }
        .pld-spinner {
          width: 28px; height: 28px; border-radius: 50%;
          border: 2.5px solid #c7d0f8; border-top-color: #4f46e5;
          animation: pldSpin .7s linear infinite;
        }
        @keyframes pldSpin { to { transform: rotate(360deg); } }
        .pld-loading-txt { font-size: 12.5px; color: #6878a8; font-weight: 600; }

        /* ══ RESPONSIVE ══ */

        /* 1024px (Nest Hub) — horizontal scroll, normal table layout */
        @media (max-width: 1024px) {
          .pld-table { min-width: 800px; }
          .pld-thead-th, .pld-tbody-td { padding: 9px 12px; }
        }

        /* 768px — tighter but still horizontal scroll */
        @media (max-width: 768px) {
          .pld-card { border-radius: 20px; }
          .pld-hdr { padding: 14px 16px 10px; border-radius: 20px 20px 0 0; }
          .pld-tbl-outer { border-radius: 0 0 20px 20px; }
          .pld-table { min-width: 720px; }
          .pld-th-lbl { font-size: 9px; }
          .pld-thead-th, .pld-tbody-td { padding: 8px 10px; }
        }

        /* 600px — stacked card layout */
        @media (max-width: 600px) {
          .pld-card { border-radius: 18px; }
          .pld-hdr { padding: 12px 14px 10px; gap: 8px; border-radius: 18px 18px 0 0; }
          .pld-ico { width: 36px; height: 36px; border-radius: 11px; }
          .pld-title { font-size: 12px; }
          .pld-view-btn { font-size: 10.5px; padding: 6px 11px; }
          .pld-tbl-outer { overflow-x: unset; border-radius: 0 0 18px 18px; }
          .pld-table, .pld-table thead, .pld-table tbody,
          .pld-table th, .pld-table td, .pld-table tr { display: block; }
          .pld-thead-tr { position: absolute; top: -9999px; left: -9999px; }
          .pld-tbody-tr {
            margin: 0 12px 10px; border-radius: 16px;
            border: 1.5px solid rgba(99,102,241,.12);
            background: rgba(255,255,255,.7); backdrop-filter: blur(4px);
            padding: 2px 0 6px; transform: none !important;
          }
          .pld-tbody-tr:hover { background: rgba(99,102,241,.04); transform: none !important; }
          .pld-tbody-td {
            display: flex; align-items: flex-start; gap: 8px;
            padding: 7px 14px; border-bottom: 1px solid rgba(99,102,241,.05); font-size: 12px;
          }
          .pld-tbody-td:last-child { border-bottom: none; }
          .pld-tbody-td::before {
            content: attr(data-label);
            font-size: 9px; font-weight: 800; letter-spacing: .5px;
            text-transform: uppercase; color: #6366f1;
            min-width: 90px; flex-shrink: 0; padding-top: 2px;
          }
          .pld-table { min-width: unset; }
          .pld-amount { font-size: 12px; }
        }
        @media (max-width: 768px) {
          .pld-status-cell {
            pointer-events: none !important;
            cursor: default !important;
          }
        }
        @media (max-width: 380px) {
          .pld-hdr { flex-direction: column; align-items: flex-start; }
          .pld-tbody-td::before { min-width: 76px; }
        }
        .txn-action-btn { width:28px; height:28px; border-radius:7px; background:#f8fafc; border:1.5px solid #e2e8f0; color:#64748b; display:flex; align-items:center; justify-content:center; transition:all .15s; text-decoration:none; cursor:pointer; }
        .txn-action-btn:hover { background:#eff6ff; border-color:#bfdbfe; color:#2563eb; }
      `}</style>
    </div>
  );
}

export default PaymentsListDashboard;